import { DHLError, axiosRequest, handleHttpResponse, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { PolicyErrorCodesEnum } from '@general/js/enums';
import { getCurrentDate } from '@general/js/utils/dateTimeUtils';
import { Services } from '@/services/Services';
import { EnvironmentVariables } from '@general/js/environmentVariables';
import ChangeCoverAbstractServices from './ChangeCoverAbstractServices';

export default class ChangeCoverServices extends ChangeCoverAbstractServices {
  /**
   * Implicitly returns a Promise for content information on change cover.
   *
   * @returns {object} - object of change cover page reference data
   */
  static async getChangeCoverLandingPageReferenceData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.LANDING.GET_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getChangeCoverLandingPageReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get reference data for add/remove person flow
   *
   * @returns {object} - object of add/remove person reference data
   */
  static async getAddRemovePersonReferenceData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.ADD_REMOVE_PERSON.GET_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getAddRemovePersonReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get reference data for capture additional information steps
   * @returns {Object}
   */
  static async getCaptureAdditionalInformationReferenceData() {
    try {
      const response = await axiosRequest().get(Services.COMMON.ADDITIONAL_INFORMATION_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getCaptureAdditionalInformationReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get reference data for Premium Impact page that's shown after Add/remove person flow is successful.
   * @returns {Object}
   */
  static async getPremiumImpactReferenceData() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.ADD_REMOVE_PERSON.GET_PREMIUM_IMPACT_REF_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getPremiumImpactReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get termination reasons from CMS
   * @returns {Object} with different types of termination reasons
   */
  static async getTerminationReasonFlexCodes(currentDate) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.GET_TERMINATION_REASONS(currentDate));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getTerminationReasonFlexCodes() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get Competitor Fund Names from CRM
   * @returns {Array} of Fund Names
   */
  static async getFundNames() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.GET_FUND_NAMES);

      if (handleHttpResponse(response)) {
        return response.data.fundNames;
      }

      throw new DHLError(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getFundNames() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get the membership details
   * @param {string} memberNumber Member number
   * @returns {Object} with different types of membership details
   */
  static async getMpCoverDetails(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.RETRIEVE_COVER_DETAILS(memberNumber, getCurrentDate()));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getMpCoverDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to check if the member is duplicate based on the search criteria of 4 fields i.e first name, last name, gender, dob
   * @param {string} firstName First name to search
   * @param {string} lastName Last name to search
   * @param {string} gender Gender to search in format `M/F`
   * @param {string} dob Date of birth to search in format `yyyy-mm-dd`
   * @returns True/False if the member is duplicate based on the search criteria
   */
  static async getDuplicatePerson(firstName, lastName, gender, dateOfBirth) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.GET_DUPLICATE_MEMBERS, { firstName, lastName, gender, dateOfBirth });

      if (handleHttpResponse(response)) {
        return response.data.isMemberDuplicate;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`isPersonDuplicate() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to submit the change cover request
   *
   * @param {string} memberNumber Member number
   * @param {object} payload Payload for change cover
   * @returns {object} with new ongoing premium/frequency details
   */
  static async submitChangeCover(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.SUBMIT_CHANGE_COVER(memberNumber), payload, {
        timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT),
      });

      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`submitChangeCover() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to raise a work item for duplicate person
   *
   * @param {string} memberNumber Member number
   * @param {object} payload Payload for duplicate person work item
   */
  static async raiseDuplicatePersonWorkItem(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.COMMON.POST_COMMON_WORK_ITEM(memberNumber), payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`raiseDuplicatePersonWorkItem() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to retrieve new policy scale from EAI
   *
   * @param {string} memberNumber Member number
   * @param {object} payload Payload for policy changes
   * @returns {object} with new policy scale details
   */
  static async retrieveNewPolicyScaleFromEAI(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.ADD_REMOVE_PERSON.RETRIEVE_NEW_SCALE(memberNumber), payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`retrieveNewPolicyScaleFromEAI() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to retrieve new policy premium from EAI
   *
   * @param {string} memberNumber Member number
   * @param {object} payload Payload for policy changes
   * @returns {object} with new policy premium details
   */
  static async retrieveNewPolicyPremiumFromEAI(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.ADD_REMOVE_PERSON.RETRIEVE_NEW_PREMIUM(memberNumber), payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`retrieveNewPolicyPremiumFromEAI() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to retrieve member product history from EAI
   *
   * @param {string} memberNumber Member number
   * @param {object} personCode Person code to fetch
   * @returns {object} with members history across all DHL policies
   */
  static async fetchMemberProductHistory(memberNumber, personCode) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.FETCH_MEMBER_HISTORY(memberNumber, personCode, getCurrentDate()));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`fetchMemberProductHistory() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Fetches all the policies on the accessing person. 
   * It is used to check if a person has multiple policies in CRM. 
   * The requesting person needs to be on the requesting policy.
   * @param {string} memberNumber membership id (policy number)
   * @param {string} accessingPersonCode
   * @returns {object}
   */
  static async getMemberPolicies(memberNumber, accessingPersonCode) {
    try {
      const response = await axiosRequest().get(Services.COMMON.GET_MEMBER_POLICIES(memberNumber, accessingPersonCode, getCurrentDate()));

      if (handleHttpResponse(response)) {
        const data = { ...response.data };

        return data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberPolicies() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
